import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Button from '../../common/Button';
import SearchableMultiSelect from '../../common/SearchableMultiSelect';
import { validateRequired, validateEmail, validatePhone } from '../../../utils/validators';
import siteService from '../../../services/siteService';
import managerService from '../../../services/managerService';

const AddTeamLeadModal = ({ isOpen, onClose, onSubmit }) => {
  const initialState = {
    name: '',
    email: '',
    contact: '',
    designation: '',
    password: '',
    site_ids: [],
    supervisor_id: ''
  };

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setFormData(initialState);
      setErrors({});
      loadAllSites();
      loadManagers();
    }
  }, [isOpen]);

  const loadManagers = async () => {
    setLoadingManagers(true);
    const result = await managerService.getAllManagers();
    if (result.success) setManagers(result.data);
    setLoadingManagers(false);
  };

  const loadAllSites = async () => {
    setLoadingSites(true);
    const result = await siteService.getAllSites();
    if (result.success) {
      setSites(result.data);
    }
    setLoadingSites(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!validateRequired(formData.name)) newErrors.name = 'Name is required';
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!validateRequired(formData.contact)) {
      newErrors.contact = 'Contact number is required';
    } else if (!validatePhone(formData.contact)) {
      newErrors.contact = 'Please enter a valid 10-digit phone number';
    }
    if (!validateRequired(formData.password)) newErrors.password = 'Password is required for new accounts';
    if (formData.site_ids.length === 0) newErrors.site_ids = 'At least one site must be selected';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        site_ids: formData.site_ids.map((id) => parseInt(id, 10)),
        supervisor_id: formData.supervisor_id ? parseInt(formData.supervisor_id, 10) : null
      };

      await onSubmit(payload);
      setFormData(initialState);
      onClose();
    } catch (error) {
      console.error('Error creating team lead:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSitesChange = (newSiteIds) => {
    setFormData(prev => ({ ...prev, site_ids: newSiteIds }));
    if (errors.site_ids) {
      setErrors(prev => ({ ...prev, site_ids: '' }));
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData(initialState);
      setErrors({});
      onClose();
    }
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  // Map sites to options including company name for clarity
  const siteOptions = sites.map(s => ({ 
    value: s.id.toString(), 
    label: s.name || s.site_name,
    subtitle: s.company?.company_name || s.company_name || 'Individual Site'
  }));

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Adding...' : 'Add Team Lead'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Team Lead" size="medium" footer={footer}>
      <form onSubmit={handleSubmit}>
        <Input label="Full Name" name="name" placeholder="e.g., Sarah Jenkins" value={formData.name} onChange={handleChange} error={errors.name} required />
        <Input label="Designation" name="designation" placeholder="Site Team Lead" value={formData.designation} onChange={handleChange} error={errors.designation} />
        <div style={gridStyle}>
          <Input label="Email" name="email" type="email" placeholder="sarah@amc.com" value={formData.email} onChange={handleChange} error={errors.email} required />
          <Input label="Phone Number" name="contact" placeholder="9876543210" value={formData.contact} onChange={handleChange} error={errors.contact} required />
        </div>

        <SearchableMultiSelect
          label="Assigned Sites"
          options={siteOptions}
          selectedValues={formData.site_ids}
          onChange={handleSitesChange}
          error={errors.site_ids}
          loading={loadingSites}
          placeholder="Search sites by name or company..."
          emptyMessage="No sites found."
          required
        />
        
        <div style={gridStyle}>
          <div className="form-group">
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px' }}>
              Assign Manager (Optional)
            </label>
            <select
              name="supervisor_id"
              style={{
                width: '100%', padding: '12px', fontSize: '14px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--surface)', color: 'var(--text-primary)', outline: 'none'
              }}
              value={formData.supervisor_id}
              onChange={handleChange}
            >
              <option value="">No Manager Assigned</option>
              {managers.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <Input label="Password" name="password" type="password" canPeekPassword placeholder="Create a temporary password" value={formData.password} onChange={handleChange} error={errors.password} required />
        </div>
      </form>
    </Modal>
  );
};


export default AddTeamLeadModal;